import categoryAdapter from 'backend/lib/adapters/categoryAdapter';
import { CustomError } from 'backend/lib/clients/httpClient';
import edithService from 'backend/services/edithService';
import searchService from 'backend/services/searchService';
import { EdithCardLayout } from 'backend/services/types/edithService.types';
import { SortOptions } from 'backend/services/types/urApiService.enums';
import { SearchParams } from 'backend/services/types/urApiService.types';
import BackgroundImage from 'components/BackgroundImage/BackgroundImage';
import CategoryTags from 'components/CategoryTags/CategoryTags';
import EdithContainer from 'components/EdithContainer/EdithContainer';
import IntroductionBanner from 'components/IntroductionBanner/IntroductionBanner';
import Layout from 'components/Layout/Layout';
import OgMetadata from 'components/OgMetadata/OgMetadata';
import ResultsList from 'components/ResultsList/ResultsList';
import { ResultListWrapper } from 'components/SearchResultWrappers/SearchResultWrappers';
import SnowFall from 'components/SnowFall/SnowFall';
import SnowRobotButton from 'components/SnowRobotButton/SnowRobotButton';
import { DataLayerPageType } from 'frontend/enums';
import { Category, CategoryItem, CategoryTag, SearchResult } from 'frontend/types';
import usePageTracking from 'frontend/utils/hooks/usePageTracking';
import useSnowOnPage from 'frontend/utils/hooks/useSnowOnPage';
import { getOrigin } from 'frontend/utils/urlHelpers';
import omit from 'lodash/omit';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getAllAdaptedCategories, getMinorityLanguageCategories } from 'utils/categoryUtil';
import { queryParamTransformer } from 'utils/transformers';
import * as Sentry from '@sentry/nextjs';
const newrelic = require('newrelic');
interface Props {
  initialSearchResult: Required<SearchResult> | null;
  initialSearchParams: SearchParams;
  category: Category;
  categorySlug: string;
  tagCategorySlug: string;
  highlightedCategories: CategoryItem[];
  minorityLanguageCategories: CategoryItem[];
  categoryHasTags: boolean;
  isOnCategoryTagPage: boolean;
  tags: CategoryTag[];
  cardLayout: EdithCardLayout;
  origin: string;
}
export const CategoryPage = ({
  highlightedCategories,
  minorityLanguageCategories,
  initialSearchResult,
  category,
  categorySlug,
  tagCategorySlug,
  categoryHasTags,
  isOnCategoryTagPage,
  tags,
  cardLayout,
  origin
}: Props) => {
  const tagCategoryPath = tagCategorySlug ? `/${tagCategorySlug}` : '';
  const url = `/bladdra/${categorySlug}${tagCategoryPath}`;
  const additionalData = {
    category: categorySlug
  };
  const SHOULD_SNOW = useSnowOnPage();
  const router = useRouter();
  const currentSlug = router.asPath.split('/').at(-1);
  const activeTag = tags.find(tag => currentSlug?.includes(tag.slug));
  usePageTracking(DataLayerPageType.CATEGORY_PAGE, additionalData);
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
        <title>{`${category.seoTitle}-${activeTag?.name} | UR Play`}</title>
        <meta name="description" content={category.seoDescription} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <link rel="canonical" href={`${origin}${url}`} />
      </Head>
      <OgMetadata title={category.name} description={category.description} type="article" sharePath={url} image={'imageUrl' in category && category.imageUrl || ''} data-sentry-element="OgMetadata" data-sentry-source-file="index.tsx" />
      <Layout categories={highlightedCategories} minorityLanguageCategories={minorityLanguageCategories} data-sentry-element="Layout" data-sentry-source-file="index.tsx">
        {SHOULD_SNOW && <SnowFall />}
        {category.imageUrlLarge && <BackgroundImage src={category.imageUrlLarge} />}
        {(!tagCategorySlug || categoryHasTags) && <IntroductionBanner title={category.name}>
            <p>{category.description}</p>
          </IntroductionBanner>}
        {tags.length > 0 && <CategoryTags activeTag={activeTag} currentSlug={currentSlug} tags={tags} categoryName={category.name.toLocaleLowerCase()} />}

        {isOnCategoryTagPage ? <ResultListWrapper data-testid="search-result-list" data-trk="subject-page-results-list" data-trk-block-type="carpet">
            <ResultsList hideMetaData={tags.length > 0} hasNextPage={false} showPlaceholders={false} products={initialSearchResult?.results ?? []} blockTracking={{
          blockIndex: 1,
          blockType: 'carpet',
          blockTitle: 'resultat'
        }} cardLayout={cardLayout} />
          </ResultListWrapper> : <>
            {category.startPage?.blocks && category.startPage?.blocks.length > 0 && <EdithContainer blocks={category.startPage.blocks} />}
          </>}
        {SHOULD_SNOW && <SnowRobotButton />}
      </Layout>
    </>;
};
const validateSlugs = (allCategories: CategoryItem[], categorySlug: string, tagCategorySlug: string) => {
  const matchingCategory = allCategories.find(c => c.slug === categorySlug);
  const tagSlugIsOk = matchingCategory?.tags.some(tag => tag.slug === tagCategorySlug && tag.level === 2);
  const slugsAreValid = matchingCategory && !Boolean(tagCategorySlug) || tagCategorySlug && tagSlugIsOk;
  return slugsAreValid;
};
export const getServerSideProps: GetServerSideProps = async ctx => {
  const {
    query,
    params
  } = ctx;
  let category: Category;
  if (!params?.categoryPath || params.categoryPath.length > 2) {
    return {
      notFound: true
    };
  }
  const [categorySlug, tagCategorySlug] = params.categoryPath as string[];
  const allCategories = await getAllAdaptedCategories();
  const currentCategory = allCategories.find(cat => cat.slug === categorySlug);
  const categoryHasTags = currentCategory ? currentCategory.tags.length > 0 : false;
  const slugsAreValid = validateSlugs(allCategories, categorySlug, tagCategorySlug);
  if (!slugsAreValid) {
    return {
      notFound: true
    };
  }
  try {
    category = categoryAdapter.adaptCategory(await edithService.getCategory(categorySlug));
  } catch (error) {
    if ((error as CustomError).response?.statusCode === 404) {
      return {
        notFound: true
      };
    }
    throw new Error(error as string);
  }
  const categoryTag = category.tags?.find(t => t.slug === tagCategorySlug);
  const initialSearchParams = {
    sort: SortOptions.TITLE,
    ...queryParamTransformer(category.tags?.length > 0 && categoryTag?.query ? {
      ...categoryTag.query,
      rows: 200
    } : category.searchQueryParams),
    ...omit(queryParamTransformer(query, 'bracket'), ['categoryPath'])
  };
  const initialSearchResult = await searchService.search(initialSearchParams, 'extended').catch(error => {
    newrelic.noticeError(error);
    Sentry.captureException(error);
    return null;
  });
  const highlightedCategories = allCategories.filter(c => c.prioritized);
  const cardLayout = categoryTag?.query.format === 'audio' ? EdithCardLayout.SQUARE : EdithCardLayout.DEFAULT;
  const origin = getOrigin(ctx);
  const minorityLanguageCategories = getMinorityLanguageCategories(allCategories);
  return {
    props: {
      initialSearchResult,
      category,
      categorySlug,
      highlightedCategories,
      minorityLanguageCategories,
      categoryHasTags,
      tagCategorySlug: tagCategorySlug || '',
      isOnCategoryTagPage: Boolean(categoryTag),
      tags: category.tags,
      cardLayout,
      origin
    }
  };
};
export default CategoryPage;